import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowRight, BadgeDollarSign, Truck, Scale, Clock } from "lucide-react";
import { PageHero, SectionHeading, reveal } from "@/components/site/primitives";
import { CTABand } from "@/pages/BusinessWaste";
import Seo from "@/components/site/Seo";
import FaqAccordion from "@/components/site/FaqAccordion";
import ServiceAreasSeo from "@/components/site/ServiceAreasSeo";
import { IMAGES } from "@/lib/data";
import { useContent } from "@/i18n/content";
import { useI18n } from "@/i18n";

const COST_ICONS = [BadgeDollarSign, Truck, Scale, Clock];

export default function DumpsterRentals() {
  const { t, localePath } = useI18n();
  const { ROLLOFF_SIZES, ROLLOFF_COST_POINTS, DUMPSTER_RENTAL_FAQS } = useContent();
  const USES = [0, 1, 2, 3, 4, 5].map((i) => t(`dr.uses.${i}`));
  const CATS = [0, 1, 2].map((i) => ({ eyebrow: t(`dr.cat${i}.eyebrow`), body: t(`dr.cat${i}.body`) }));
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: DUMPSTER_RENTAL_FAQS.map((f) => ({
      "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <>
      <Seo
        title={t("dr.seoTitle")}
        description={t("dr.seoDesc")}
        path="/dumpster-rentals"
        image={IMAGES.rolloffDumpster}
        jsonLd={faqJsonLd}
      />

      <PageHero
        crumb={t("dr.crumb")}
        eyebrow={t("dr.eyebrow")}
        title={t("dr.title")}
        highlight={t("dr.highlight")}
        subtitle={t("dr.subtitle")}
      >
        <div className="flex flex-wrap items-center gap-5">
          <div className="rounded-2xl bg-white/[0.08] border border-white/15 px-6 py-4" data-testid="rolloff-starting-price">
            <span className="block font-mono-tech text-[10px] uppercase tracking-widest text-emerald-300">{t("dr.startingAt")}</span>
            <span className="font-display font-extrabold text-3xl text-white">$289<span className="text-lg text-white/60 font-semibold">/14-day*</span></span>
          </div>
          <Link to={localePath("/quick-quote")} className="group inline-flex items-center gap-2 px-6 py-4 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold hover:bg-emerald-400 transition-all" data-testid="rolloff-hero-quote">
            {t("cta.requestQuote")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <p className="mt-3 text-[11px] text-white/45">{t("dr.priceFootnote")}</p>
      </PageHero>


      <section className="relative bg-[#FBFAF8] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionHeading eyebrow={t("dr.introEyebrow")} title={t("dr.introTitle")} highlight={t("dr.introHighlight")} />
            <p className="mt-6 text-slate-600 leading-relaxed max-w-xl">
              {t("dr.introBody")}
            </p>
            <div className="mt-7 grid sm:grid-cols-2 gap-3">
              {USES.map((u) => (
                <div key={u} className="flex items-center gap-2.5 text-sm text-[#0B0F14]">
                  <span className="grid place-items-center w-5 h-5 rounded-full bg-emerald-600/10 text-emerald-700"><Check size={13} strokeWidth={3} /></span>{u}
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }} className="relative">
            <div className="bg-[#F0EFEA] rounded-3xl p-6 shadow-xl max-w-md mx-auto">
              <img loading="lazy" decoding="async" src={IMAGES.rolloffDumpster} alt="Roll-off dumpster" className="w-full rounded-xl" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative bg-[#F0EFEA] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow={t("dr.sizesEyebrow")} title={t("dr.sizesTitle")} highlight={t("dr.sizesHighlight")} className="mb-14" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ROLLOFF_SIZES.map((d, i) => (
              <motion.div key={d.size} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="bg-white border border-[#E7E5DF] rounded-3xl p-7 shadow-soft flex flex-col hover:-translate-y-1 hover:border-emerald-600/40 transition-[transform,border-color,box-shadow] duration-300 transform-gpu" data-testid={`rolloff-${i}`}>
                <Link to={localePath(`/dumpster-rentals/${d.slug}`)} className="flex flex-col flex-1">
                  <div className="mb-5 grid place-items-center h-48">
                    <img loading="lazy" decoding="async" src={d.img} alt={`${d.size} roll-off dumpster — ${d.dims}`} className="max-h-48 w-auto object-contain" />
                  </div>
                  <h3 className="font-display font-bold text-2xl text-[#0B0F14]">{d.size}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed flex-1">{d.desc}</p>
                  <p className="mt-4 font-mono-tech text-xs uppercase tracking-wider text-emerald-700">{d.dims}</p>
                  <p className="mt-2 font-display font-extrabold text-lg text-[#0B0F14]">{t("common.from")} $289<span className="text-xs font-semibold text-slate-500"> /14-day</span></p>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-semibold text-[#0B0F14]">{t("dr.viewDetails")} <ArrowRight size={16} /></span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-[#FBFAF8] py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B0F14] leading-[1.05]">
            {t("dr.anyTitleA")} <span className="text-emerald-600">{t("dr.anyTitleB")}</span>
          </h2>
          <p className="mt-6 text-slate-600 leading-relaxed">
            {t("dr.anyBody")}
          </p>
        </div>
      </section>

      <section className="relative bg-[#0E3B3B] py-24 text-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-8">
          {CATS.map((c, i) => (
            <motion.div key={c.eyebrow} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="rounded-3xl bg-white/[0.05] border border-white/10 p-8" data-testid={`rolloff-cat-${i}`}>
              <span className="font-display font-bold text-2xl text-emerald-400">{c.eyebrow}</span>
              <h3 className="font-display font-extrabold text-2xl text-white mb-4">{t("dr.catWord")}</h3>
              <p className="text-white/75 text-sm leading-relaxed">{c.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Cost / pricing — targets "dumpster rental cost Miami" */}
      <section className="relative bg-[#FBFAF8] py-24" data-testid="rolloff-cost">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow={t("dr.costEyebrow")} title={t("dr.costTitle")} highlight={t("dr.costHighlight")} className="mb-6 mx-auto" />
          <p className="max-w-3xl mx-auto text-center text-slate-600 leading-relaxed mb-14">
            {t("dr.costIntro")}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ROLLOFF_COST_POINTS.map((c, i) => {
              const Icon = COST_ICONS[i];
              return (
                <div key={c.label} className="bg-white border border-[#E7E5DF] rounded-3xl p-7 shadow-soft" data-testid={`cost-point-${i}`}>
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-emerald-600/10 text-emerald-700 mb-4"><Icon size={22} /></span>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500">{c.label}</div>
                  <div className="font-display font-extrabold text-2xl text-[#0B0F14] mt-1">{c.value}</div>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{c.note}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ — progressive disclosure, targets long-tail search */}
      <FaqAccordion
        eyebrow={t("dr.faqEyebrow")}
        title={t("dr.faqTitle")}
        highlight={t("dr.faqHighlight")}
        faqs={DUMPSTER_RENTAL_FAQS}
      />

      {/* Subtle local SEO */}
      <ServiceAreasSeo service="roll-off dumpster rentals for construction, demolition and cleanout projects" />

      <CTABand />
    </>
  );
}
